import { OctLabel, OctNumberField } from '@oceantech/oceantech-ui';
import { Form, Formik, FormikProps, getIn } from 'formik';
import { useEffect, useRef } from 'react';
import { Button, Col, Modal, Row } from 'react-bootstrap';
import { toast } from 'react-toastify';
import * as Yup from 'yup';
import { useAppContext } from '../../../../appContext/AppContext';
import Autocomplete from '../../../../component/AutocompleteObjectV2';
import {
	CODE,
	DEFAULT_PAGE_INDEX,
	DEFAULT_PAGE_SIZE,
	MESSAGE,
	RESPONSE_MESSAGE,
} from '../../../../utils/Constant';
import { getErrorMessage } from '../../../../utils/FormatUtils';
import { getObsComplexCategory, getObsValue } from '../../../../utils/ServicesUtils';
import { getPriceByObjectType } from '../../../modals/modal-ke-thuoc/utils';
import { BASE_URL } from '../../../services/ThanhToanService';

type Props = {
	open: boolean;
	onClose: () => void;
	onSave: (data: any) => void;
	itemEdit?: any;
};

const TransferInfo = ({ open, onClose, onSave, itemEdit }: Props) => {
	const { setIsLoading } = useAppContext();

	const formikRef = useRef<FormikProps<any>>(null);

	const validationSchema = Yup.object().shape({
		hospital: Yup.mixed().required(MESSAGE.REQUIRED).nullable(),
		distance: Yup.number().required(MESSAGE.REQUIRED),
	});

	useEffect(() => {
		if (itemEdit?.id) {
			getDetail(itemEdit);
		}
	}, [itemEdit?.id]);

	const getDetail = async (itemEdit: any) => {
		try {
			setIsLoading(true);
			const response = await getObsValue({
				orderId: itemEdit?.orderId,
				encounterId: itemEdit?.encounterId,
			});

			if (CODE.SUCCESS !== response.code) {
				toast.error(getErrorMessage(response));
				return;
			}

			formikRef.current?.setValues({
				distance: response.data?.distance,
				unitPrice: itemEdit?.svcPrice,
				hospital: response.data?.termModelId
					? {
							termModelId: response.data?.termModelId,
							hospital: response.data?.hospital,
					  }
					: null,
			});
		} catch (error) {
			console.error(error);
			toast.error(RESPONSE_MESSAGE.ERROR);
		} finally {
			setIsLoading(false);
		}
	};

	const onSubmit = async (values: any) => {
		onSave(values);
	};

	const calculatePrice = (selectedOption: any) => {
		if (!selectedOption) {
			formikRef.current?.setFieldValue('unitPrice', '');
			return;
		}

		const unitPrice = getPriceByObjectType(
			{
				...selectedOption,
				servicePrice: selectedOption.svcFee,
				bhytPrice: selectedOption.svcFeeIns,
				hospitalPrice: selectedOption.svcFeeHospital,
			},
			itemEdit?.objectTypeCode
		);
		formikRef.current?.setFieldValue('unitPrice', unitPrice);
	};

	return (
		<Modal show={open} onHide={onClose} centered>
			<Modal.Header closeButton>
				<Modal.Title>Thông tin vận chuyển</Modal.Title>
			</Modal.Header>

			<Formik<any>
				initialValues={{
					hospital: null,
					distance: '',
				}}
				innerRef={formikRef}
				onSubmit={onSubmit}
				validationSchema={validationSchema}
			>
				{({ values, errors, touched, setFieldValue }) => (
					<Form>
						<Modal.Body>
							<Row>
								<Col sm={12}>
									<OctLabel label="Bệnh viện" isRequired />
									<Autocomplete
										options={[]}
										value={values?.hospital}
										name="hospital"
										searchFunction={getObsComplexCategory}
										searchObject={{
											path: BASE_URL,
											pageIndex: DEFAULT_PAGE_INDEX,
											pageSize: DEFAULT_PAGE_SIZE,
											code: 'dmchuyentuyen',
										}}
										urlData="data.content"
										onChange={(selectedOption) => {
											setFieldValue('hospital', selectedOption);
											calculatePrice(selectedOption);
										}}
										getOptionLabel={(option) =>
											option?.hospital
												? (option.hospital ?? '').split('|')[1]
												: ''
										}
										getOptionValue={(option) => option?.termModelId}
										isSrcoll
										labelSearch="keyword"
										errors={errors?.hospital}
										touched={touched?.hospital}
									/>
								</Col>
							</Row>
							<Row className="mt-3">
								<Col sm={6}>
									<OctLabel label="Đơn giá" />
									<OctNumberField
										name="unitPrice"
										value={values?.unitPrice}
										disabled
									/>
								</Col>
								<Col sm={6}>
									<OctLabel label="Khoảng cách (km)" isRequired />
									<OctNumberField
										name="distance"
										value={values?.distance}
										onChange={(value: number) => {
											setFieldValue('distance', value);
										}}
										error={getIn(errors, 'distance')}
										touched={getIn(touched, 'distance')}
									/>
								</Col>
							</Row>
						</Modal.Body>

						<Modal.Footer>
							<Button className="btn-danger" type="button" onClick={onClose}>
								Đóng
							</Button>
							<Button className="btn-fill spaces px-16" type="submit">
								Lưu
							</Button>
						</Modal.Footer>
					</Form>
				)}
			</Formik>
		</Modal>
	);
};

export default TransferInfo;
